import { useState, useMemo } from 'react';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import { ICONS } from '@ui/icons/types';
import Text from '@ui/text/text/text';
import styles from '../../../widget.module.css';
import Icon from '@ui/icons/icon';
import ManageWatermarkModal from './modals/manage-watermark-modal';
import DeleteWatermarkModal from './modals/delete-watermark-modal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCurrentWatermarkSet } from '@/store/slices/watermarks';
import { selectWatermarkSets } from '@/store/slices/watermarks/selectors';
import { selectProjectSettings, getWorkingProjectUuid, getIsSettingsUpdating } from '@/store/slices/project/selectors';
import useLoadChannelWatermarks from '@/micros/edit-project/grid-areas/main/settings/content/security/watermark/hooks/use-load-channel-watermarks';
import { selectProjectWatermarkSet } from '@/store/slices/settings/selectors';
import { updateProjectSettings } from '@/store/slices/project';
import type { IWatermarkSet } from '@/types/watermarks.types';

const WatermarkWidget = () => {
  const watermarkSets = useAppSelector(selectWatermarkSets);
  const watermarkSetUuid = useAppSelector(selectProjectWatermarkSet);
  const projectSettings = useAppSelector(selectProjectSettings);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);

  const dispatch = useAppDispatch();

  useLoadChannelWatermarks();

  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const savedWatermark = useMemo(
    () => watermarkSets?.find((ws) => ws.uuid === watermarkSetUuid) ?? null,
    [watermarkSets, watermarkSetUuid]
  );

  const handleOpen = () => {
    if (savedWatermark) {
      dispatch(setCurrentWatermarkSet(savedWatermark));
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSelect = async (watermark: IWatermarkSet) => {
    if (!projectUuid || !projectSettings) return;
    await dispatch(
      updateProjectSettings({
        event_uuid: projectUuid,
        settings: { ...projectSettings, watermark_set: watermark.uuid },
      })
    ).unwrap();
    handleClose();
  };

  const handleDelete = async () => {
    if (!projectUuid || !projectSettings) return;
    await dispatch(
      updateProjectSettings({
        event_uuid: projectUuid,
        settings: { ...projectSettings, watermark_set: undefined },
      })
    ).unwrap();
    setConfirmDelete(false);
  };

  return (
    <div className={styles['widget']}>
      <Container
        design="GlobalCont1"
        background="var(--gray-900)"
        borderRadius="8px"
        className={`${savedWatermark ? 'flex-column' : 'flex ai-center jc-space-between'} gap-16 padding-16`}
        loading={isSettingsUpdating}
        opacity={isSettingsUpdating ? '0.6' : '1'}
      >
        <Text className="H6">Watermarking</Text>

        {savedWatermark ? (
          <Container
            width="100%"
            design="GlobalCont2"
            borderRadius="8px"
            className="padding-16 flex ai-center jc-space-between"
          >
            <Container className="flex gap-16">
              <Container
                className="flex ai-center jc-center"
                width="69px"
                height="40px"
                design="GlobalCont3"
                borderRadius="8px"
              >
                <Icon name={ICONS.WATERMARK} size={24} />
              </Container>
              <Container>
                <Text className="S3" design="GlobalIcoText1">
                  {savedWatermark.name}
                </Text>
                <Text className="S3" design="GlobalIcoText3">
                  {savedWatermark.elements.length + ' items'}
                </Text>
              </Container>
            </Container>
            <div className="flex ai-center gap-8">
              <Button
                type="button"
                designSize="small"
                design="LocalBttnSec"
                className="radius-full square-32 flex-column ai-center jc-center"
                onClick={handleOpen}
                disabled={isSettingsUpdating}
                disabledOpacity={0.6}
              >
                <Icon name={ICONS.EDIT} size={16} color="var(--gray-200)" />
              </Button>
              <Button
                designSize="small"
                design="LocalBttnSec"
                className="radius-full square-32 flex-column ai-center jc-center"
                onClick={() => setConfirmDelete(true)}
                disabled={isSettingsUpdating}
                disabledOpacity={0.6}
              >
                <Icon name={ICONS.DELETE_TRASH_BIN} size={12} />
              </Button>
            </div>
          </Container>
        ) : (
          <Button
            design="LocalBttnSec"
            onClick={handleOpen}
            hoverBackgroundColor="var(--gray-1000)"
            className={`${styles['add-button']} flex ai-center gap-6`}
            height="32px"
            disabled={isSettingsUpdating}
            disabledOpacity={0.6}
          >
            <Icon name={ICONS.ADD} size={12} color="var(--global-ico-text-1)" />
            <Text design="GlobalIcoText1" className="L2">
              Add
            </Text>
          </Button>
        )}
      </Container>

      <ManageWatermarkModal open={open} onComplete={handleClose} onSelect={handleSelect} />
      <DeleteWatermarkModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDelete={handleDelete}
        working={isSettingsUpdating}
        mode="project"
      />
    </div>
  );
};

export default WatermarkWidget;
